// Zugriff auf den Schachserver. Ändernde Anfragen tragen den Pflicht-Header,
// den fremde Webseiten nicht setzen dürfen.

export interface GameRow {
  id: number;
  white: string | null;
  black: string | null;
  event: string | null;
  site: string | null;
  date: string | null;
  year: number | null;
  result: string | null;
  eco: string | null;
  plies: number;
  error: string | null;
  inWallpaper: boolean;
}

export interface Page {
  total: number;
  games: GameRow[];
  /** Partien mit fertiger Computeranalyse */
  reviewed: number[];
}

export interface PlyEval {
  cp: number | null;
  mate: number | null;
  best: string | null;
}

export interface SideSummary {
  inaccuracies: number;
  mistakes: number;
  blunders: number;
  acpl: number | null;
  accuracy: number | null;
}

export interface ReviewResult {
  status: 'none' | 'running' | 'done' | 'error';
  available?: boolean;
  done: number;
  total: number;
  nodes: number;
  evals: (PlyEval | null)[];
  judgements: { ply: number; judgement: 'inaccuracy' | 'mistake' | 'blunder'; best: string | null }[];
  white: SideSummary | null;
  black: SideSummary | null;
  error: string | null;
}

export interface Ply {
  fen: string;
  uci: string | null;
  san: string | null;
  last: string | null;
}

export interface GameDetail {
  game: GameRow;
  pgn: string;
  tags: Record<string, string>;
  plies: Ply[];
  error: string;
  /** eigene Varianten: Zugfolgen (UCI) ab Ausgangsstellung */
  variations: string[][];
}

export interface ImportReport {
  games: number;
  imported: number;
  duplicates: number;
  problems: { index: number; white: string | null; black: string | null; error: string }[];
}

export interface WallpaperSettings {
  secondsPerMove: number;
  endPauseSeconds: number;
  boardSize: number;
  position: 'center' | 'left' | 'right';
  theme: 'night' | 'brown' | 'blue';
  brightness: number;
  showNames: boolean;
  showYear: boolean;
  showMove: boolean;
  showLastMove: boolean;
  showCoordinates: boolean;
  animation: 'none' | 'fast' | 'normal' | 'slow';
  afterGame: 'next' | 'repeat' | 'random';
  display: number;
  pauseOnBattery: boolean;
  pieces: PieceSet;
  mode: WallpaperMode;
  /** Zahl der Bretter im Modus Simultan */
  boards: number;
  /** Sprache der Texte im Lernmodus, folgt der Oberfläche */
  lang: 'de' | 'en' | 'fi';
}

export type WallpaperMode = 'single' | 'gallery' | 'simul' | 'sheet' | 'trail' | 'poster' | 'learn';

/** Lerndaten eines Halbzugs (Lesson.java): Eröffnung aus der lichess-Liste, Urteil der Computeranalyse */
export interface LessonPly {
  eco: string | null;
  name: string | null;
  book: boolean;
  exit: boolean;
  bookMoves: string[];
  cp: number | null;
  mate: number | null;
  judgement: 'inaccuracy' | 'mistake' | 'blunder' | null;
  better: string | null;
  arrow: string | null;
}

export type PieceSet = 'genelec' | 'cburnett';

export interface WallpaperStatus {
  running: boolean;
  pid: number | null;
  games: number;
  renderer: string;
  rendererFound: boolean;
}

import { t } from './i18n';

export async function call<T>(method: string, url: string, body?: BodyInit, json = false): Promise<T> {
  const headers: Record<string, string> = {};
  if (method !== 'GET') headers['X-Schachserver'] = '1';
  if (json) headers['Content-Type'] = 'application/json';
  const res = await fetch(url, { method, headers, body });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j.error) msg = j.error;
    } catch {
      /* kein JSON */
    }
    // bekannte Meldungen des Servers in der gewählten Sprache; der Status hilft, Vorübergehendes zu erkennen
    throw Object.assign(new Error(t(msg)), { status: res.status });
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export interface Recognition {
  boardFen: string;
  flipped: boolean;
  board: { x: number; y: number; size: number };
  unsure: string[];
  width: number;
  height: number;
}

export interface AutostartState {
  enabled: boolean;
  available: boolean;
  reason: string | null;
}

export interface Setup {
  autostart: { server: AutostartState; wallpaper: AutostartState };
  engine: { path: string; available: boolean; name: string };
  renderer: string;
  dataDir: string;
  packaged: boolean;
}

export const api = {
  games: (q: string, offset = 0, limit = 100) =>
    call<Page>('GET', `/api/games?q=${encodeURIComponent(q)}&offset=${offset}&limit=${limit}`),
  game: (id: number) => call<GameDetail>('GET', `/api/games/${id}`),
  importFiles: (files: File[]) => {
    const fd = new FormData();
    files.forEach(f => fd.append('file', f));
    return call<ImportReport>('POST', '/api/import', fd);
  },
  importText: (text: string) => call<ImportReport>('POST', '/api/import', text),
  playMove: (fen: string, req: { elo: number; bot?: unknown }) =>
    call<{ bestmove: string | null; cp?: number | null; mate?: number | null }>('POST', '/api/play/move', JSON.stringify({ fen, ...req }), true),
  bots: () => call<{ stockfish: boolean; maia: boolean; rodent: boolean }>('GET', '/api/bots'),
  saveAnalysis: (id: number, lines: string[][]) =>
    call<void>('PUT', `/api/games/${id}/analysis`, JSON.stringify(lines), true),
  review: (id: number) => call<ReviewResult>('GET', `/api/games/${id}/review`),
  requestReview: (id: number) => call<ReviewResult>('POST', `/api/games/${id}/review`),
  recognize: (png: Blob, frame?: { x: number; y: number; size: number }) => {
    const fd = new FormData();
    fd.append('image', png, 'bild.png');
    if (frame) {
      fd.append('x', String(Math.round(frame.x)));
      fd.append('y', String(Math.round(frame.y)));
      fd.append('size', String(Math.round(frame.size)));
    }
    return call<Recognition>('POST', '/api/recognize', fd);
  },
  setup: () => call<Setup>('GET', '/api/setup'),
  setAutostart: (target: 'server' | 'wallpaper', on: boolean) =>
    call<AutostartState>('PUT', '/api/setup/autostart', JSON.stringify({ target, on }), true),
  remove: (id: number) => call<void>('DELETE', `/api/games/${id}`),
  setWallpaper: (id: number, on: boolean) =>
    call<WallpaperStatus>('PUT', `/api/games/${id}/wallpaper`, JSON.stringify({ on }), true),
  wallpaper: () =>
    call<{ settings: WallpaperSettings; status: WallpaperStatus; games: GameRow[] }>('GET', '/api/wallpaper'),
  saveWallpaper: (s: WallpaperSettings) =>
    call<WallpaperSettings>('PUT', '/api/wallpaper/settings', JSON.stringify(s), true),
  start: () => call<WallpaperStatus>('POST', '/api/wallpaper/start'),
  stop: () => call<WallpaperStatus>('POST', '/api/wallpaper/stop'),
  lesson: (id: number) => call<LessonPly[]>('GET', `/api/games/${id}/lesson`),
  lessons: () => call<{ done: number; total: number }>('GET', '/api/lessons'),
};
